import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { FiPlus, FiSend, FiEye } from 'react-icons/fi';

const TABS = ['Subscribers', 'Lists', 'Templates', 'Campaigns'];

const Marketing = () => {
  const { success, error: showError } = useToast();
  const [tab, setTab] = useState('Subscribers');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const [subscribers, setSubscribers] = useState([]);
  const [lists, setLists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  const [subForm, setSubForm] = useState({ email: '', firstName: '', lastName: '', consentSource: 'manual' });
  const [listForm, setListForm] = useState({ name: '' });
  const [templateForm, setTemplateForm] = useState({ code: '', name: '', subject: '', bodyHtml: '', templateType: 'marketing' });
  const [campaignForm, setCampaignForm] = useState({ name: '', subject: '', emailTemplateId: '', subscriberListIds: [] });

  const [selectedListId, setSelectedListId] = useState(null);
  const [listMembers, setListMembers] = useState(null);
  const [addMemberSubId, setAddMemberSubId] = useState('');

  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [recipients, setRecipients] = useState(null);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/comms/marketing/subscribers'),
      apiClient.get('/comms/marketing/lists'),
      apiClient.get('/comms/marketing/templates'),
      apiClient.get('/comms/marketing/campaigns'),
    ]).then(([subRes, listRes, tplRes, campRes]) => {
      setSubscribers(subRes.data.data);
      setLists(listRes.data.data);
      setTemplates(tplRes.data.data);
      setCampaigns(campRes.data.data);
    }).catch((err) => showError(getErrorMessage(err, 'Could not load marketing data')))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, []); // eslint-disable-line react-hooks/exhaustive-deps

  const subscriberEmail = (id) => subscribers.find((s) => s.id === id)?.email || id;
  const listName = (id) => lists.find((l) => l.id === id)?.name || id;

  // ---- Subscribers ----
  const handleAddSubscriber = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...subForm };
      if (!payload.firstName) delete payload.firstName;
      if (!payload.lastName) delete payload.lastName;
      await apiClient.post('/comms/marketing/subscribers', payload);
      success('✅ Subscriber added');
      setShowForm(false);
      setSubForm({ email: '', firstName: '', lastName: '', consentSource: 'manual' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not add subscriber')); }
  };

  const handleUnsubscribe = async (id) => {
    try {
      await apiClient.post(`/comms/marketing/subscribers/${id}/unsubscribe`);
      success('✅ Unsubscribed');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not unsubscribe')); }
  };

  // ---- Lists ----
  const handleAddList = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/comms/marketing/lists', listForm);
      success('✅ List created');
      setShowForm(false);
      setListForm({ name: '' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not create list')); }
  };

  const viewList = async (list) => {
    setSelectedListId(list.id);
    setListMembers(null);
    try {
      const { data } = await apiClient.get(`/comms/marketing/lists/${list.id}/members`);
      setListMembers(data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not load list members')); }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!addMemberSubId) return;
    try {
      await apiClient.post(`/comms/marketing/lists/${selectedListId}/members`, { subscriberId: addMemberSubId });
      success('✅ Added to list');
      setAddMemberSubId('');
      const { data } = await apiClient.get(`/comms/marketing/lists/${selectedListId}/members`);
      setListMembers(data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not add to list')); }
  };

  // ---- Templates ----
  const handleAddTemplate = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/comms/marketing/templates', templateForm);
      success('✅ Template created');
      setShowForm(false);
      setTemplateForm({ code: '', name: '', subject: '', bodyHtml: '', templateType: 'marketing' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not create template')); }
  };

  // ---- Campaigns ----
  const toggleCampaignList = (listId) => {
    setCampaignForm((prev) => ({
      ...prev,
      subscriberListIds: prev.subscriberListIds.includes(listId)
        ? prev.subscriberListIds.filter((id) => id !== listId)
        : [...prev.subscriberListIds, listId],
    }));
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    if (campaignForm.subscriberListIds.length === 0) { showError('Select at least one list'); return; }
    try {
      const payload = { ...campaignForm };
      if (!payload.emailTemplateId) delete payload.emailTemplateId;
      await apiClient.post('/comms/marketing/campaigns', payload);
      success('✅ Campaign created as draft');
      setShowForm(false);
      setCampaignForm({ name: '', subject: '', emailTemplateId: '', subscriberListIds: [] });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not create campaign')); }
  };

  const handleSendCampaign = async (campaignId) => {
    if (!window.confirm('Send this campaign now? This sends a real email to every subscribed member of the selected list(s) — this cannot be undone.')) return;
    try {
      await apiClient.post(`/comms/marketing/campaigns/${campaignId}/send`);
      success('✅ Campaign sent');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not send campaign')); }
  };

  const viewCampaign = async (campaign) => {
    setSelectedCampaign(campaign);
    setRecipients(null);
    try {
      const { data } = await apiClient.get(`/comms/marketing/campaigns/${campaign.id}/recipients`);
      setRecipients(data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not load recipients')); }
  };

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="page-header">
        <h1>Marketing</h1>
        <p>Subscribers, lists, templates, and email campaigns</p>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); setShowForm(false); setSelectedListId(null); setSelectedCampaign(null); }}
            style={{
              padding: '0.75rem 1rem', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: 600, color: tab === t ? '#3b82f6' : '#6b7280',
              borderBottom: tab === t ? '2px solid #3b82f6' : '2px solid transparent',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Subscribers' && (
        <section className="page-section">
          <div className="section-header">
            <h2>Subscribers</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'Add Subscriber'}
            </button>
          </div>
          {showForm && (
            <form onSubmit={handleAddSubscriber} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field"><label>Email *</label><input required type="email" value={subForm.email} onChange={(e) => setSubForm(p => ({ ...p, email: e.target.value }))} /></div>
              <div className="form-field"><label>First Name</label><input value={subForm.firstName} onChange={(e) => setSubForm(p => ({ ...p, firstName: e.target.value }))} /></div>
              <div className="form-field"><label>Last Name</label><input value={subForm.lastName} onChange={(e) => setSubForm(p => ({ ...p, lastName: e.target.value }))} /></div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Add</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Email</th><th>Name</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
                ) : subscribers.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No subscribers yet</td></tr>
                ) : subscribers.map((s) => (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 600 }}>{s.email}</td>
                    <td>{s.firstName || s.lastName ? `${s.firstName || ''} ${s.lastName || ''}`.trim() : '—'}</td>
                    <td><span className="badge" style={{ background: s.status === 'subscribed' ? '#10b981' : '#9ca3af', color: 'white' }}>{s.status}</span></td>
                    <td>
                      {s.status === 'subscribed' && (
                        <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleUnsubscribe(s.id)}>Unsubscribe</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Lists' && (
        <>
          <section className="page-section">
            <div className="section-header">
              <h2>Subscriber Lists</h2>
              <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
                <FiPlus size={14} /> {showForm ? 'Cancel' : 'New List'}
              </button>
            </div>
            {showForm && (
              <form onSubmit={handleAddList} style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
                <input required value={listForm.name} onChange={(e) => setListForm({ name: e.target.value })} placeholder="List name" style={{ flex: 1, padding: '0.5rem' }} />
                <button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Create</button>
              </form>
            )}
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Name</th><th>Actions</th></tr></thead>
                <tbody>
                  {lists.length === 0 ? (
                    <tr><td colSpan={2} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No lists yet</td></tr>
                  ) : lists.map((l) => (
                    <tr key={l.id}>
                      <td style={{ fontWeight: 600 }}>{l.name}</td>
                      <td><button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => viewList(l)}><FiEye size={14} /> View Members</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {selectedListId && (
            <section className="page-section">
              <div className="section-header">
                <h2>Members — {listName(selectedListId)}</h2>
                <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => setSelectedListId(null)}>Close</button>
              </div>
              <form onSubmit={handleAddMember} style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                <select value={addMemberSubId} onChange={(e) => setAddMemberSubId(e.target.value)} style={{ flex: 1, padding: '0.5rem' }}>
                  <option value="">-- select a subscriber --</option>
                  {subscribers.map((s) => <option key={s.id} value={s.id}>{s.email}</option>)}
                </select>
                <button type="submit" className="btn btn-sm btn-primary" style={{ width: 'auto' }}>Add to List</button>
              </form>
              {listMembers === null ? <p style={{ color: '#6b7280' }}>Loading...</p> : listMembers.length === 0 ? (
                  <p style={{ color: '#6b7280' }}>No members in this list yet.</p>
                ) : (
                  <div className="table-container">
                    <table className="data-table">
                      <thead><tr><th>Email</th><th>Name</th><th>Status</th></tr></thead>
                      <tbody>
                        {listMembers.map((m) => (
                          <tr key={m.id}>
                            <td style={{ fontWeight: 600 }}>{m.email}</td>
                            <td>{m.firstName || m.lastName ? `${m.firstName || ''} ${m.lastName || ''}`.trim() : '—'}</td>
                            <td><span className="badge" style={{ background: m.status === 'subscribed' ? '#10b981' : '#9ca3af', color: 'white' }}>{m.status}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
            </section>
          )}
        </>
      )}

      {tab === 'Templates' && (
        <section className="page-section">
          <div className="section-header">
            <h2>Email Templates</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'New Template'}
            </button>
          </div>
          {showForm && (
            <form onSubmit={handleAddTemplate} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field"><label>Code *</label><input required value={templateForm.code} onChange={(e) => setTemplateForm(p => ({ ...p, code: e.target.value }))} placeholder="newsletter_sept" /></div>
              <div className="form-field"><label>Name *</label><input required value={templateForm.name} onChange={(e) => setTemplateForm(p => ({ ...p, name: e.target.value }))} /></div>
              <div className="form-field full-width"><label>Subject *</label><input required value={templateForm.subject} onChange={(e) => setTemplateForm(p => ({ ...p, subject: e.target.value }))} /></div>
              <div className="form-field">
                <label>Type</label>
                <select value={templateForm.templateType} onChange={(e) => setTemplateForm(p => ({ ...p, templateType: e.target.value }))}>
                  <option value="marketing">Marketing</option>
                  <option value="transactional">Transactional</option>
                </select>
              </div>
              <div className="form-field full-width"><label>Body (HTML) *</label><textarea required rows={6} value={templateForm.bodyHtml} onChange={(e) => setTemplateForm(p => ({ ...p, bodyHtml: e.target.value }))} placeholder="<p>Hello...</p>" style={{ width: '100%', padding: '0.5rem', fontFamily: 'monospace', fontSize: '0.85rem' }} /></div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Create</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Code</th><th>Name</th><th>Subject</th><th>Type</th></tr></thead>
              <tbody>
                {templates.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No templates yet</td></tr>
                ) : templates.map((t) => (
                  <tr key={t.id}>
                    <td style={{ fontFamily: 'monospace' }}>{t.code}</td>
                    <td style={{ fontWeight: 600 }}>{t.name}</td>
                    <td>{t.subject}</td>
                    <td style={{ textTransform: 'capitalize' }}>{t.templateType}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Campaigns' && (
        <>
          <section className="page-section">
            <div className="section-header">
              <h2>Campaigns</h2>
              <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
                <FiPlus size={14} /> {showForm ? 'Cancel' : 'New Campaign'}
              </button>
            </div>
            <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '1rem' }}>
              ⚠️ Sending a campaign dispatches real emails through your configured SMTP to every subscribed member of the selected list(s) — this is not a simulation.
            </p>
            {showForm && (
              <form onSubmit={handleCreateCampaign} className="form-grid" style={{ marginBottom: '1.5rem' }}>
                <div className="form-field"><label>Name *</label><input required value={campaignForm.name} onChange={(e) => setCampaignForm(p => ({ ...p, name: e.target.value }))} /></div>
                <div className="form-field"><label>Subject *</label><input required value={campaignForm.subject} onChange={(e) => setCampaignForm(p => ({ ...p, subject: e.target.value }))} /></div>
                <div className="form-field">
                  <label>Template</label>
                  <select value={campaignForm.emailTemplateId} onChange={(e) => setCampaignForm(p => ({ ...p, emailTemplateId: e.target.value }))}>
                    <option value="">-- none (plain subject-only body) --</option>
                    {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div className="form-field full-width">
                  <label>Send To Lists *</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.3rem' }}>
                    {lists.map((l) => (
                      <label key={l.id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.7rem', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb', textTransform: 'none', fontWeight: 400 }}>
                        <input type="checkbox" checked={campaignForm.subscriberListIds.includes(l.id)} onChange={() => toggleCampaignList(l.id)} style={{ width: 'auto' }} />
                        {l.name}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Create Draft</button></div>
              </form>
            )}
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Name</th><th>Subject</th><th>Status</th><th>Sent</th><th>Actions</th></tr></thead>
                <tbody>
                  {campaigns.length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No campaigns yet</td></tr>
                  ) : campaigns.map((c) => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 600 }}>{c.name}</td>
                      <td>{c.subject}</td>
                      <td><span className="badge" style={{ background: c.status === 'sent' ? '#10b981' : c.status === 'sending' ? '#f59e0b' : '#9ca3af', color: 'white' }}>{c.status}</span></td>
                      <td>{c.sentAt ? new Date(c.sentAt).toLocaleString() : '—'}</td>
                      <td>
                        <div className="actions">
                          {c.status === 'draft' && (
                            <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleSendCampaign(c.id)}>
                              <FiSend size={14} /> Send Now
                            </button>
                          )}
                          {c.status === 'sent' && (
                            <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => viewCampaign(c)}>
                              <FiEye size={14} /> Results
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {selectedCampaign && (
            <section className="page-section">
              <div className="section-header">
                <h2>Results — {selectedCampaign.name}</h2>
                <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => setSelectedCampaign(null)}>Close</button>
              </div>
              {recipients === null ? <p style={{ color: '#6b7280' }}>Loading...</p> : (
                <div className="table-container">
                  <table className="data-table">
                    <thead><tr><th>Recipient</th><th>Delivery</th><th>Opens</th><th>Clicks</th></tr></thead>
                    <tbody>
                      {recipients.map((r) => (
                        <tr key={r.id}>
                          <td>{subscriberEmail(r.subscriberId)}</td>
                          <td><span className="badge" style={{ background: r.deliveryStatus === 'sent' ? '#10b981' : '#ef4444', color: 'white' }}>{r.deliveryStatus}</span></td>
                          <td>{r.openCount}</td>
                          <td>{r.clickCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </>
      )}
    </motion.div>
  );
};

export default Marketing;